if (!window.FlavorsPicker) {
  class FlavorsPicker {
    static QUANTITY_TEMPALTE_ID = "flavors-picker-input";
    static ERROR_TEMPLATE_ID = "flavors-picker-error-template";
    static SELECTION_LIST_TEMPLATE_ID = "flavors-picker-selection-list-template";
    static DETAILS_CONTAINER_SELECTOR = ".product-details";
    static VARIANT_PICKER_SELECT_SELECTOR = ".variant-option__select";
    static SUMMARY_INPUT_SELECTOR = ".fp-input-summary .gpo-input";
    static GPO_SWATCH_SELECTOR = '[data-type="image-swatches_multiple"]';
    static GPO_SELECTED_VALUES_SELECTOR = ".gpo-selected-values";
    static IMAGE_SWATCHES_CONTAINER_SELECTOR = ".fp-image-swatches";
    static ADD_TO_CART_SELECTOR = ".add-to-cart-button";

    /** @type {number} */
    #currentSize = 0;
    #productDetails = null;
    #variantSelect = null;
    #summaryInput = null;
    #statusMessage = null;
    #selectionList = null;
    #observer = null;
    #swatchesInitialized = false;
    #validationListenersSetup = false;
    #hasAttemptedSubmit = false;

    constructor() {}

    init = () => {
      this.#setupElements();
      
      // If we don't have product details yet, it might be a timing issue (dynamic loading).
      // We'll let the observer retry init when needed.
      if (this.#productDetails) {
        this.#defineCurrentSize();
        this.#setupVariantListener();
        this.#setupAddToCartValidation();
      }
      
      // Always setup the observer - it handles swatches AND retrying initialization
      this.#observeGPO();

      // Listen for Shopify section updates
      document.addEventListener("shopify:section:load", () => {
        this.#swatchesInitialized = false; // Allow re-initialization
        this.init();
      });
    };

    #setupElements = () => {
      // Clear old references to avoid stale memory
      this.#productDetails = document.querySelector(FlavorsPicker.DETAILS_CONTAINER_SELECTOR);
      if (!this.#productDetails) return;

      this.#variantSelect = this.#productDetails.querySelector(FlavorsPicker.VARIANT_PICKER_SELECT_SELECTOR);
      this.#summaryInput = this.#productDetails.querySelector(FlavorsPicker.SUMMARY_INPUT_SELECTOR);
      this.#selectionList = this.#productDetails.querySelector("#flavors-picker-selections");
      
      // Pre-initialize the status message container if possible
      this.#ensureStatusMessage();
    };

    #defineCurrentSize = () => {
      if (!this.#variantSelect) return;
      const selectedValue = this.#variantSelect.value || "";
      const match = selectedValue.match(/(\d+)/);
      this.#currentSize = match ? parseInt(match[0], 10) : 0;
      this.updateAllSwatches();
    };

    #setupVariantListener = () => {
      if (!this.#variantSelect) return;
      // Remove old listener if re-initializing
      this.#variantSelect.removeEventListener("change", this.#onVariantChange);
      this.#variantSelect.addEventListener("change", this.#onVariantChange);
    };

    #onVariantChange = () => {
      this.#hasAttemptedSubmit = false;
      this.#defineCurrentSize();
    };

    #observeGPO = () => {
      if (this.#observer) return;

      const target = document.body;
      const config = { childList: true, subtree: true };

      this.#observer = new MutationObserver(() => {
        // 1. Check if we need to (re)initialize core elements
        if (!this.#productDetails) {
          this.#setupElements();
          if (this.#productDetails) {
            this.#defineCurrentSize();
            this.#setupVariantListener();
            this.#setupAddToCartValidation();
          }
        }

        // 2. Re-discover summary input if GPO loaded late
        if (!this.#summaryInput && this.#productDetails) {
          this.#summaryInput = this.#productDetails.querySelector(FlavorsPicker.SUMMARY_INPUT_SELECTOR);
          if (this.#summaryInput) {
            console.log('[FlavorsPicker] Summary input discovered via observer:', this.#summaryInput);
            this.updateSummary(); // Sync immediately
          }
        }

        // 3. Re-patch forms in case they were replaced (Shopify Section API)
        if (typeof this.patchForms === "function") {
          this.patchForms();
        }

        // 4. Check for GPO swatches
        const gpoSwatches = document.querySelectorAll(FlavorsPicker.GPO_SWATCH_SELECTOR);
        if (gpoSwatches.length > 0 && !this.#swatchesInitialized) {
          this.#initializeSwatches(gpoSwatches);
        }
      });

      this.#observer.observe(target, config);

      // Initial checks
      const existingSwatches = document.querySelectorAll(FlavorsPicker.GPO_SWATCH_SELECTOR);
      if (existingSwatches.length > 0) {
        this.#initializeSwatches(existingSwatches);
      }
    };

    #initializeSwatches = (swatches) => {
      const template = document.getElementById(FlavorsPicker.QUANTITY_TEMPALTE_ID);
      if (!template) return;
      
      this.#swatchesInitialized = true;

      swatches.forEach((checkbox) => {
        const container = checkbox.closest(".gpo-swatch, .image-swatches, .globo-swatch");
        if (!container || container.querySelector("fb-swatch-input")) return;

        const clone = template.content.cloneNode(true);
        const fbInput = clone.querySelector("fb-swatch-input");
        fbInput.dataset.flavor = checkbox.value;
        fbInput.setAttribute("data-flavor", checkbox.value);

        container.style.position = "relative";
        container.appendChild(clone);

        const swatchInput = container.querySelector("fb-swatch-input");
        if (swatchInput && typeof swatchInput.setDisabled === "function") {
          swatchInput.setDisabled(!checkbox.checked);
        }

        checkbox.addEventListener("change", () => {
          const si = container.querySelector("fb-swatch-input");
          if (!si) return;

          if (typeof si.setDisabled === "function") {
            si.setDisabled(!checkbox.checked);
          }

          if (typeof si.setValue !== "function") return;

          if (checkbox.checked) {
            if (si.value === 0) si.setValue(1);
          } else {
            si.setValue(0);
          }
        });

        if (checkbox.checked) {
          if (swatchInput && typeof swatchInput.setValue === "function") {
            swatchInput.setValue(1);
          }
        }
      });
    };

    updateAllSwatches = () => {
      const total = this.getTotalCount();
      const fbInputs = document.querySelectorAll("fb-swatch-input");
      fbInputs.forEach((input) => {
        if (typeof input.updateState === "function") {
          input.updateState(total, this.#currentSize);
        }
      });
      this.#disableUncheckedSwatches(total);
      this.updateSummary();
      this.#updateSelectionList();
      this.#updateStatusMessage();
      this.#syncAddToCartState();
    };

    #disableUncheckedSwatches = (total) => {
      const allCheckboxes = document.querySelectorAll(FlavorsPicker.GPO_SWATCH_SELECTOR);
      allCheckboxes.forEach((checkbox) => {
        const container = checkbox.closest(".gpo-swatch, .image-swatches, .globo-swatch");
        if (!container) return;

        if (!checkbox.checked && total >= this.#currentSize) {
          container.classList.add("fp-swatch--locked");
          checkbox.disabled = true;
        } else {
          container.classList.remove("fp-swatch--locked");
          checkbox.disabled = false;
        }
      });
    };

    getTotalCount = () => {
      let total = 0;
      document.querySelectorAll("fb-swatch-input").forEach((input) => {
        total += parseInt(input.value || 0, 10);
      });
      return total;
    };

    getCurrentSize = () => {
      return this.#currentSize;
    };

    #getSelections = () => {
      const selections = [];
      document.querySelectorAll("fb-swatch-input").forEach((input) => {
        const val = parseInt(input.value || 0, 10);
        if (val > 0) {
          selections.push({ flavor: input.dataset.flavor, count: val });
        }
      });
      return selections;
    };

    updateSummary = () => {
      // ALWAYS re-query — GPO may load/replace the input at any time
      const scope = this.#productDetails || document;
      this.#summaryInput = scope.querySelector(FlavorsPicker.SUMMARY_INPUT_SELECTOR)
                        || scope.querySelector('.fp-input-summary input')
                        || scope.querySelector('input.fp-input-summary')
                        || scope.querySelector('.gpo-container input[type="text"]');

      if (!this.#summaryInput) {
        console.log('[FlavorsPicker] Summary input not found yet — GPO may still be loading');
        return;
      }

      const selections = this.#getSelections();
      const text = selections
        .map((s) => `${s.flavor} | ${s.count}`)
        .join(", ");

      this.#summaryInput.value = text;

      // Bubble events so GPO registers the value
      this.#summaryInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.#summaryInput.dispatchEvent(new Event("change", { bubbles: true }));
      this.#summaryInput.dispatchEvent(new Event("blur", { bubbles: true }));
    };

    #ensureSelectionList = () => {
      if (this.#selectionList && document.body.contains(this.#selectionList))
        return this.#selectionList;

      const template = document.getElementById(FlavorsPicker.SELECTION_LIST_TEMPLATE_ID);
      if (!template) return null;

      const clone = template.content.cloneNode(true);
      this.#selectionList = clone.querySelector(".fp-selection-list");

      const gpoSelectedValues = document.querySelector(FlavorsPicker.GPO_SELECTED_VALUES_SELECTOR);
      if (gpoSelectedValues && gpoSelectedValues.parentNode) {
        gpoSelectedValues.parentNode.insertBefore(clone, gpoSelectedValues.nextSibling);
      }

      return this.#selectionList;
    };

    #updateSelectionList = () => {
      const listEl = this.#ensureSelectionList();
      if (!listEl) return;

      const selections = this.#getSelections();

      if (selections.length === 0) {
        listEl.style.display = "none";
        return;
      }

      listEl.style.display = "flex";
      listEl.innerHTML = selections
        .map(
          (s) =>
            `<span class="fp-selection-item"><strong>${s.flavor}</strong>&nbsp;×&nbsp;${s.count}</span>`,
        )
        .join("");
    };

    #ensureStatusMessage = () => {
      // 1. Identify the active GPO container
      // Use the productDetails scope to avoid picking containers from other sections (e.g., related products)
      const scope = this.#productDetails || document;
      
      // Find the flavor container within our scope
      const visibleSwatches = Array.from(scope.querySelectorAll(".fp-image-swatches"))
                                   .find(el => el.offsetParent !== null) || 
                               scope.querySelector(".fp-image-swatches");
      
      const specificContainer = visibleSwatches?.closest('.gpo-container, .gpo-form__group');
      
      // Fallback: the last visible GPO container in OUR scope
      const allVisibleContainers = Array.from(scope.querySelectorAll('.gpo-container, .gpo-form__group'))
                                        .filter(el => el.offsetParent !== null);
      const gpoContainer = specificContainer || allVisibleContainers[allVisibleContainers.length - 1];

      // 2. Try to find existing message WITHIN OUR SCOPE
      let msg = scope.querySelector(".fp-status-message");

      // 3. Position or Move
      if (gpoContainer && gpoContainer.parentNode) {
        if (!msg) {
          const template = document.getElementById(FlavorsPicker.ERROR_TEMPLATE_ID);
          if (template) {
            const clone = template.content.cloneNode(true);
            gpoContainer.parentNode.insertBefore(clone, gpoContainer.nextSibling);
            msg = scope.querySelector(".fp-status-message");
          }
        } else if (msg.previousElementSibling !== gpoContainer) {
          // Reposition to follow the CURRENT active container in this product section
          gpoContainer.parentNode.insertBefore(msg, gpoContainer.nextSibling);
        }
      }

      // 4. Absolute Fallback: ATC area within our scope
      if (!msg || !document.body.contains(msg)) {
        const atcArea = scope.querySelector(".product-form-buttons") || 
                        scope.querySelector(FlavorsPicker.ADD_TO_CART_SELECTOR);
        if (atcArea && atcArea.parentNode) {
          const template = document.getElementById(FlavorsPicker.ERROR_TEMPLATE_ID);
          if (template) {
             const clone = template.content.cloneNode(true);
             atcArea.parentNode.insertBefore(clone, atcArea);
             msg = scope.querySelector(".fp-status-message");
          }
        }
      }

      return msg;
    };

    #updateStatusMessage = () => {
      const msg = this.#ensureStatusMessage();
      if (!msg) return;

      // Ensure we have a valid size to calculate against
      if (this.#currentSize <= 0) this.#defineCurrentSize();

      // If we still have no size, we shouldn't show anything (it might not be a bundle)
      if (this.#currentSize <= 0) {
        msg.style.display = "none";
        return;
      }

      const total = this.getTotalCount();
      const remaining = this.#currentSize - total;
      
      // Determine state
      let state = 'hidden';
      if (total === this.#currentSize) {
        state = 'success';
      } else if (this.#hasAttemptedSubmit) {
        state = 'error';
      } else if (total > 0) {
        state = 'info';
      }

      // Apply state to UI
      if (state === 'hidden') {
        msg.style.display = "none";
        return;
      }

      msg.style.display = "block";
      msg.classList.remove("fp-status--error", "fp-status--success", "fp-status--neutral", "fp-atc-valid");

      switch(state) {
        case 'success':
          msg.classList.add("fp-status--success");
          msg.textContent = `All ${this.#currentSize} pieces selected. You're all set!`;
          break;
        case 'error':
          msg.classList.add("fp-status--error");
          msg.textContent = `You have picked ${total} items of ${this.#currentSize}. Please add ${remaining} more to continue.`;
          break;
        case 'info':
        default:
          msg.classList.add("fp-status--neutral");
          msg.textContent = `${total} of ${this.#currentSize} pieces selected.`;
          break;
      }
    };

    #syncAddToCartState = () => {
      const total = this.getTotalCount();
      const isValid = this.#currentSize > 0 && total === this.#currentSize;

      document
        .querySelectorAll(FlavorsPicker.ADD_TO_CART_SELECTOR)
        .forEach((btn) => {
          if (isValid) {
            btn.classList.remove("fp-atc-blocked");
            btn.classList.add("fp-atc-valid");
            btn.removeAttribute("disabled");
            btn.removeAttribute("aria-disabled");
          } else {
            btn.classList.add("fp-atc-blocked");
            btn.classList.remove("fp-atc-valid");
            btn.setAttribute("disabled", "disabled");
            btn.setAttribute("aria-disabled", "true");
          }
        });
    };

    patchForms = () => {
      document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
        if (form._fpPatched) return;
        form._fpPatched = true;

        const isInvalid = () => {
          if (this.#currentSize <= 0) this.#defineCurrentSize();
          if (this.#currentSize <= 0) return false;
          return this.getTotalCount() !== this.#currentSize;
        };

        const showError = () => {
          this.#hasAttemptedSubmit = true;
          this.#updateStatusMessage();

          const msg = this.#ensureStatusMessage();
          if (msg) {
            msg.scrollIntoView({ behavior: "smooth", block: "center" });
            msg.classList.remove("error-shake");
            void msg.offsetWidth;
            msg.classList.add("error-shake");
          }
        };

        const originalSubmit = form.submit.bind(form);
        const originalRequestSubmit = form.requestSubmit ? form.requestSubmit.bind(form) : null;

        form.submit = function () {
          if (isInvalid()) {
            showError();
            return;
          }
          return originalSubmit();
        };

        if (originalRequestSubmit) {
          form.requestSubmit = function (submitter) {
            if (isInvalid()) {
              showError();
              return;
            }
            return originalRequestSubmit(submitter);
          };
        }
      });
    };

    #setupAddToCartValidation = () => {
      if (this.#validationListenersSetup) return;
      this.#validationListenersSetup = true;

      this.patchForms();
      
      document.addEventListener(
        "click",
        (e) => {
          const target = e.target;
          if (!target) return;

          const isInvalid = () => {
            if (this.#currentSize <= 0) this.#defineCurrentSize();
            if (this.#currentSize <= 0) return false;
            return this.getTotalCount() !== this.#currentSize;
          };

          const showError = () => {
            this.#hasAttemptedSubmit = true;
            this.#updateStatusMessage();

            const msg = this.#ensureStatusMessage();
            if (msg) {
              msg.scrollIntoView({ behavior: "smooth", block: "center" });
              msg.classList.remove("error-shake");
              void msg.offsetWidth;
              msg.classList.add("error-shake");
            }
          };

          const isAtcArea = target.closest(FlavorsPicker.ADD_TO_CART_SELECTOR) || 
                           target.closest('.product-form-buttons') ||
                           target.closest('add-to-cart-component');

          if (isAtcArea && isInvalid()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            showError();
          }
        },
        true,
      );

      document.addEventListener(
        "submit",
        (e) => {
          const target = e.target;
          if (target && target.matches && target.matches('form[data-type="add-to-cart-form"]')) {
            const isInvalid = () => {
              if (this.#currentSize <= 0) this.#defineCurrentSize();
              if (this.#currentSize <= 0) return false;
              return this.getTotalCount() !== this.#currentSize;
            };

            const showError = () => {
              this.#hasAttemptedSubmit = true;
              this.#updateStatusMessage();

              const msg = this.#ensureStatusMessage();
              if (msg) {
                msg.scrollIntoView({ behavior: "smooth", block: "center" });
                msg.classList.remove("error-shake");
                void msg.offsetWidth;
                msg.classList.add("error-shake");
              }
            };

            if (isInvalid()) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              showError();
              return false;
            }
          }
        },
        true,
      );
    };
  }

  window.flavorsPickerInstance = new FlavorsPicker();
  window.flavorsPickerInstance.init();
}

if (!customElements.get("fb-swatch-input")) {
  class FbSwatchInput extends HTMLElement {
    #btnPlus = null;
    #btnMinus = null;
    #input = null;
    #value = 0;
    #disabled = true;

    get value() {
      return this.#value;
    }

    connectedCallback() {
      this.#init();
    }

    #init = () => {
      this.#btnPlus = this.querySelector("[data-component='btn-plus']");
      this.#btnMinus = this.querySelector("[data-component='btn-minus']");
      this.#input = this.querySelector("[data-component='input']");
      if (!this.#input) return;
      this.#input.value = this.#value;

      this.#attachEventListeners();
      this.#applyDisabledState();
      this.#updateButtons();
    };

    #attachEventListeners = () => {
      this.#btnPlus?.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Allow incrementing even if unchecked, unless we're at the limit
        const fp = window.flavorsPickerInstance;
        if (fp.getTotalCount() < fp.getCurrentSize()) {
          this.increment();
        }
      });

      this.#btnMinus?.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.#disabled) this.decrement();
      });

      this.#input?.addEventListener("change", () => {
        const fp = window.flavorsPickerInstance;
        let val = parseInt(this.#input.value) || 0;
        if (val < 0) val = 0;

        const otherTotal = fp.getTotalCount() - this.#value;
        const maxAllowed = fp.getCurrentSize() - otherTotal;
        if (val > maxAllowed) val = Math.max(0, maxAllowed);

        const oldValue = this.#value;
        this.#value = val;
        this.#input.value = this.#value;

        // Sync with checkbox
        if (this.#value > 0 && oldValue === 0) {
          this.#syncCheckbox(true);
        } else if (this.#value === 0 && oldValue > 0) {
          this.#syncCheckbox(false);
        }

        fp.updateAllSwatches();
      });
    };

    /**
     * Helper to find and toggle the GPO checkbox
     */
    #syncCheckbox = (shouldCheck) => {
      const container = this.closest(".gpo-swatch, .image-swatches, .globo-swatch");
      if (!container) return;

      const checkbox = container.querySelector('[data-type="image-swatches_multiple"]');
      if (checkbox && checkbox.checked !== shouldCheck) {
        checkbox.checked = shouldCheck;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    };

    increment = () => {
      const oldValue = this.#value;
      this.#value++;
      this.#input.value = this.#value;

      if (this.#value > 0 && oldValue === 0) {
        this.#syncCheckbox(true);
      }

      window.flavorsPickerInstance.updateAllSwatches();
    };

    decrement = () => {
      if (this.#value > 0) {
        this.#value--;
        this.#input.value = this.#value;

        if (this.#value === 0) {
          this.#syncCheckbox(false);
        }

        window.flavorsPickerInstance.updateAllSwatches();
      }
    };

    setValue = (val) => {
      this.#value = Math.max(0, val);
      if (this.#input) this.#input.value = this.#value;
      window.flavorsPickerInstance.updateAllSwatches();
    };

    setDisabled = (isDisabled) => {
      this.#disabled = isDisabled;
      this.#applyDisabledState();
    };

    #applyDisabledState = () => {
      // We no longer strictly disable the entire control to allow '+' and manual input to work
      if (this.#disabled) {
        this.classList.add("fp-swatch-input--disabled");
      } else {
        this.classList.remove("fp-swatch-input--disabled");
      }
      const fp = window.flavorsPickerInstance;
      this.updateState(fp.getTotalCount(), fp.getCurrentSize());
    };

    updateState = (total, max) => {
      this.#updateButtons(total, max);
    };

    #updateButtons = (total, max) => {
      // 1. Ensure the number input is ALWAYS enabled so users can type to select
      if (this.#input) {
        this.#input.disabled = false;
        // Also ensure pointer-events are active
        this.#input.style.pointerEvents = "auto";
      }

      // 2. Plus button is enabled if total < max
      if (this.#btnPlus) {
        this.#btnPlus.disabled = total >= max;
      }

      // 3. Minus button is enabled if value > 0 AND it's not disabled by checkbox
      if (this.#btnMinus) {
        this.#btnMinus.disabled = this.#disabled || this.#value <= 0;
      }
    };
  }

  customElements.define("fb-swatch-input", FbSwatchInput);
}
